/**
 * JSON backup export/import (PLAN §9, point 5).
 *
 * This is not a new format. `PersistedState` is already the localStorage shape
 * *and* the backup format — flat records, ISO date strings, no `undefined`, an
 * explicit `v` with a tested migration ladder — precisely so a file exported
 * here imports straight into the future iOS app. That is the entire sync story:
 * there is no server.
 *
 * So export is "read the persisted state and pretty-print it", and import is
 * `parsePersistedState`, which already validates and migrates. Inventing a
 * separate envelope would create a second shape to keep in step with the first.
 */

import type { PersistedState } from "@schema/persist.ts";
import { PERSIST_VERSION, parsePersistedState } from "@schema/persist.ts";

/** `mcu-road-backup-2026-08-03.json` — sorts chronologically in a downloads folder. */
export function backupFilename(today: string): string {
  return `mcu-road-backup-${today}.json`;
}

/**
 * Pretty-printed rather than minified. A backup is a file a person may open,
 * read, and hand to a future version of themselves; 2-space JSON costs a few
 * hundred bytes and makes it inspectable.
 */
export function serializeBackup(state: PersistedState): string {
  return `${JSON.stringify(state, null, 2)}\n`;
}

export interface BackupSummary {
  state: PersistedState;
  watchedCount: number;
  version: number;
  /** True when the file was written by an older build and had to be migrated. */
  migrated: boolean;
}

/**
 * Parse and validate a backup file.
 *
 * Throws with the underlying reason rather than a generic failure — "expected
 * object, received array" tells the reader they picked the Letterboxd CSV by
 * mistake, which is the single most likely wrong file to choose here.
 */
export function readBackup(json: string): BackupSummary {
  let declaredVersion: number = PERSIST_VERSION;
  try {
    const raw: unknown = JSON.parse(json);
    if (raw && typeof raw === "object" && "v" in raw && typeof raw.v === "number") {
      declaredVersion = raw.v;
    }
  } catch {
    // Leave it to parsePersistedState to produce the real error below.
  }

  const state = parsePersistedState(json);
  return {
    state,
    watchedCount: Object.keys(state.watched).length,
    version: declaredVersion,
    migrated: declaredVersion !== PERSIST_VERSION,
  };
}
